import { Link } from 'react-router-dom'

import Button from '../../Button'
import type { TCheckoutCreditItem } from '../../../hooks/useCheckoutSummary'

type Props = {
  creditBalance: number
  creditDifference: number
  creditItems: TCheckoutCreditItem[]
  creditsToPurchase: number | null
  creditsTotal: number
  isDisabled?: boolean
  isMaster: boolean
  setCreditsToPurchase: (creditsToPurchase: number) => void
  shouldShowPurchaseAdHocCredits: boolean
}

const CheckoutCreditSummary = (props: Props) => {
  const {
    creditBalance,
    creditDifference,
    creditItems,
    creditsToPurchase,
    creditsTotal,
    isDisabled,
    isMaster,
    setCreditsToPurchase,
    shouldShowPurchaseAdHocCredits,
  } = props

  return (
    <>
      <div className="rounded-md border border-gray-200">
        <div className="border-b border-gray-200 px-4 py-3">
          <h4 className="text-sm font-medium text-black">
            Credits to be deducted
          </h4>
        </div>
        <div className="divide-y divide-gray-200">
          {creditItems.map((item) => (
            <div
              className="flex items-center justify-between px-4 py-3"
              key={item.description}
            >
              <p className="text-sm text-gray-700">{item.description}</p>
              <p className="text-sm text-gray-700">
                {item.amount} credit{item.amount !== 1 ? 's' : ''}
              </p>
            </div>
          ))}
          <div className="flex items-center justify-between px-4 py-3">
            <p className="text-sm font-semibold tracking-tight text-black">
              Total credits to be deducted
            </p>
            <p className="text-sm font-semibold tracking-tight text-black">
              {creditsTotal} credit{creditsTotal !== 1 ? 's' : ''}
            </p>
          </div>
        </div>
      </div>

      {creditDifference > 0 && !creditsToPurchase && (
        <div className="rounded-md border border-red-200 bg-red-50 mt-4 px-4 py-3">
          <p className="text-sm font-semibold tracking-tight text-red-700">
            Insufficient credit balance
          </p>
          <p className="mt-1 text-sm text-red-700">
            {isMaster ? (
              <>
                You do not have enough credits to create this upload. The total
                for this upload is {creditsTotal} credit
                {creditsTotal === 1 ? '' : 's'} but you{' '}
                {creditBalance > 0 ? (
                  <>
                    only have {creditBalance} credit
                    {creditBalance === 1 ? ' ' : 's '}
                  </>
                ) : (
                  "don't have any credits "
                )}
                in your account.
              </>
            ) : (
              <>
                You do not have enough credits to create this upload. Please
                contact your master dealer to purchase credits.
              </>
            )}
          </p>
        </div>
      )}

      {shouldShowPurchaseAdHocCredits && (
        <>
          <div className="mt-4 flex flex-wrap gap-x-3 gap-y-2">
            <Button
              color="primary"
              size="sm"
              disabled={isDisabled}
              onClick={() => setCreditsToPurchase(creditDifference)}
            >
              Add {creditDifference} credit
              {creditDifference === 1 ? '' : 's'} to checkout
            </Button>
            <Button
              as={isDisabled ? 'button' : Link}
              to={isDisabled ? undefined : '/credits'}
              color="white"
              size="sm"
              disabled={isDisabled}
            >
              Go to credit packages
            </Button>
          </div>
          <p className="mt-3 text-xs text-gray-600">
            Credits will be charged at your single-file rate. Credits added to
            checkout will be shown below and will only be charged once you press
            the "Create upload" button.
          </p>
        </>
      )}
    </>
  )
}

export default CheckoutCreditSummary
